import { routes } from '@/config/routes'
import { captureException } from '@sentry/react-router'
import { type CSSProperties, type HTMLAttributes, useCallback, useEffect } from 'react'
import { Link, generatePath, isRouteErrorResponse, useRouteError } from 'react-router'
import { Button } from '../common/button'
import { ScrollArea } from '../common/scrollArea'
import { RefreshIcon } from '../icons/refresh'
import { Content, Page } from '../layout/wrappers'

export const ErrorPage = () => {
  const error = useRouteError()

  useEffect(() => {
    if (!isRouteErrorResponse(error) && error instanceof Error && error.stack) {
      captureException(error)
    }
  }, [error])

  const onRefresh = useCallback(() => {
    window.location.reload()
  }, [])

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFoundPage />
    } else {
      console.error(error)
    }
  }

  return (
    <ErrorWrapper>
      <h2 className={`${ERROR_TITLE_CLASS} text-2xl md:text-4.5xl`}>
        {(error instanceof Error ? error.name : null) ?? 'Something went wrong'}
      </h2>
      {error instanceof Error && error.message && <p className="text-lg md:text-2xl">{error.message}</p>}
      <div className="mt-2 flex flex-col items-center gap-2">
        <Button size="lg" className="w-fit" asChild>
          <Link to={generatePath(routes.root)}>Go to homepage</Link>
        </Button>
        <span>or</span>
        <Button
          variant="default"
          size="default"
          className="inline-flex items-center gap-1 rounded-full"
          onClick={onRefresh}
        >
          <RefreshIcon />
          <span className="underline">Refresh page</span>
        </Button>
      </div>
    </ErrorWrapper>
  )
}

const NotFoundPage = () => {
  return (
    <Page>
      <ScrollArea className="h-full">
        <Content className="relative flex min-h-[max(32rem,calc(100dvh_-_var(--top-bar-height)))] flex-col overflow-hidden">
          <FloatingVaults />

          <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-9 py-8 text-center">
            <div className="flex flex-col items-center gap-5">
              <h1 className={`${ERROR_TITLE_CLASS} [font-size:clamp(7.5rem,24vw,16rem)]`}>404</h1>
              <p className="font-medium text-body-foreground text-lg md:text-xl">Page not found</p>
            </div>

            <Button variant="secondary" size="lg" className="w-fit" asChild>
              <Link to={generatePath(routes.root)}>Back to homepage</Link>
            </Button>
          </div>
        </Content>
      </ScrollArea>
    </Page>
  )
}

const ErrorWrapper = ({ children }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <Page>
      <ScrollArea className="h-full">
        <Content className="relative flex min-h-[max(32rem,calc(100dvh_-_var(--top-bar-height)))] flex-col overflow-hidden">
          <FloatingVaults />

          <div className="relative z-10 flex flex-1 items-center justify-center py-8">
            <div className="flex max-w-xl flex-col items-center gap-4 px-4 text-center md:gap-5">{children}</div>
          </div>
        </Content>
      </ScrollArea>
    </Page>
  )
}

const ERROR_TITLE_CLASS =
  'bg-gradient-to-b from-primary-400 to-primary-600 bg-clip-text font-bold text-transparent leading-none'

const FloatingVaults = () => {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {FLOATING_VAULTS.map((vault) => (
        <picture
          key={vault.className}
          className={`not-found-vault pointer-events-none absolute block select-none drop-shadow-xl ${vault.className}`}
          style={
            {
              '--vault-opacity': vault.opacity,
              '--vault-float-duration': vault.floatDuration,
              '--vault-phase-duration': vault.phaseDuration,
              '--vault-phase-delay': vault.delay,
              '--vault-rotation': vault.rotation,
            } as CSSProperties
          }
        >
          <source srcSet="/vault-3d.webp" type="image/webp" />
          <img
            src="/vault-3d.png"
            alt=""
            className="not-found-vault-image pointer-events-none block h-auto w-full select-none"
            loading="eager"
            draggable={false}
          />
        </picture>
      ))}
    </div>
  )
}

const FLOATING_VAULTS = [
  {
    className: 'top-[9%] left-[12%] w-[clamp(5.25rem,11vw,10rem)]',
    opacity: 0.9,
    floatDuration: '8.5s',
    phaseDuration: '12.5s',
    delay: '-1.2s',
    rotation: '-15deg',
  },
  {
    className: 'top-[18%] right-[9%] w-[clamp(5rem,10.5vw,9.5rem)]',
    opacity: 0.9,
    floatDuration: '9.75s',
    phaseDuration: '15s',
    delay: '-6.5s',
    rotation: '-13deg',
  },
  {
    className: 'top-[42%] left-[5%] w-[clamp(4.75rem,9.5vw,8.75rem)] max-xs:hidden',
    opacity: 0.9,
    floatDuration: '7.75s',
    phaseDuration: '13.75s',
    delay: '-9s',
    rotation: '-16deg',
  },
  {
    className: 'right-[7%] bottom-[13%] w-[clamp(5rem,10.5vw,9.5rem)]',
    opacity: 0.8,
    floatDuration: '10.5s',
    phaseDuration: '16.25s',
    delay: '-3.25s',
    rotation: '-14deg',
  },
  {
    className: 'bottom-[10%] left-[7%] w-[clamp(4.75rem,9.5vw,8.75rem)] max-sm:hidden',
    opacity: 0.8,
    floatDuration: '9s',
    phaseDuration: '14.5s',
    delay: '-11s',
    rotation: '-17deg',
  },
  {
    className: 'top-[10%] right-[29%] w-[clamp(4.75rem,9.5vw,8.75rem)] max-md:hidden',
    opacity: 0.8,
    floatDuration: '8s',
    phaseDuration: '17.5s',
    delay: '-7.75s',
    rotation: '-12deg',
  },
] as const
